/**
 * Web message alerts — the chime / OS-notification layer for incoming
 * messages (the "user doesn't have the app or their phone is charging"
 * case).
 *
 * Delivery: the backend emits a user-room UNREAD_COUNT socket event on
 * every message (it already drives the nav badge); useUnreadCountSocketSync
 * calls alertNewMessage() from that handler. No backend involvement.
 *
 * Behavior: visible tab → chime + toast; hidden tab with notification
 * permission granted → OS notification instead (tagged, so a burst of
 * messages replaces one notification rather than stacking).
 *
 * The toggle is DEVICE-LOCAL (localStorage) on purpose: sound is a
 * per-machine concern (office vs home), and messages intentionally have no
 * eventType in the backend notification-preference registry — persisting
 * this there would mean inventing a registry entry for an event that
 * deliberately doesn't exist.
 *
 * Failure modes handled: browsers block audio until the user's first
 * interaction with the page (expected, logged at debug, never thrown), and
 * the Notification API may be absent entirely (older Safari) — feature-
 * detected, falls back to the chime path.
 */
import { toastInfo } from '@/utils/toast.jsx'
import { createLogger } from '@/utils/logger'

const logger = createLogger('messageAlerts')

export const MESSAGE_ALERTS_STORAGE_KEY = 'gg-message-alerts-enabled'
const CHIME_URL = '/sounds/message-chime.mp3'
const NOTIFICATION_TAG = 'gg-new-message'

/** Default ON — only an explicit opt-out disables alerts. */
export const messageAlertsEnabled = (): boolean =>
  window.localStorage.getItem(MESSAGE_ALERTS_STORAGE_KEY) !== 'off'

export const setMessageAlertsEnabled = (enabled: boolean): void => {
  window.localStorage.setItem(MESSAGE_ALERTS_STORAGE_KEY, enabled ? 'on' : 'off')
}

export const notificationsSupported = (): boolean => 'Notification' in window

let chime: HTMLAudioElement | null = null

export const playMessageChime = (): void => {
  chime ??= new Audio(CHIME_URL)
  chime.currentTime = 0
  chime.play().catch((err: unknown) => {
    // Autoplay policy: sound stays blocked until the user's first
    // interaction with the page. Expected pre-interaction state.
    logger.debug({ err }, 'chime blocked by autoplay policy')
  })
}

/**
 * Fire the appropriate alert for one incoming message. The caller decides
 * WHETHER to alert (new message, not the open conversation); this decides
 * HOW.
 */
export const alertNewMessage = (): void => {
  if (!messageAlertsEnabled()) return

  if (
    document.hidden &&
    notificationsSupported() &&
    Notification.permission === 'granted'
  ) {
    const notification = new Notification('New message on GearGhost', {
      body: 'Open Messages to reply.',
      tag: NOTIFICATION_TAG,
    })
    notification.onclick = () => {
      window.focus()
      notification.close()
    }
    return
  }

  playMessageChime()
  toastInfo('New message')
}

/**
 * Ask for OS-notification permission if the user hasn't answered yet.
 * Called when the settings toggle is switched ON — never on page load
 * (permission prompts out of nowhere get denied and can't be re-asked).
 */
export const requestNotificationPermission = (): void => {
  if (!notificationsSupported() || Notification.permission !== 'default') return
  Notification.requestPermission().catch((err: unknown) => {
    logger.warn({ err }, 'notification permission request failed')
  })
}
